/**
 * Gaze Tracking Module
 *
 * Integrates WebGazer.js for real webcam eye tracking,
 * with One Euro Filter for smooth + responsive output,
 * fixation detection, and mouse fallback.
 */

import type { GazePoint, GazeState } from '@/types'
import { gazeBridge } from '@/lib/gaze-bridge'
import { OneEuroFilter2D } from '@/lib/one-euro-filter'

// One Euro Filter params — tuned for webcam gaze
const GAZE_FILTER_MIN_CUTOFF = 0.8 // lower = smoother when still
const GAZE_FILTER_BETA = 0.5 // higher = more responsive when moving
const GAZE_FILTER_DCUTOFF = 1.0

// Mouse filter — lighter touch since mouse is already precise
const MOUSE_FILTER_MIN_CUTOFF = 3.0
const MOUSE_FILTER_BETA = 0.8

const FIXATION_MIN_DURATION_MS = 250
const SACCADE_VELOCITY_THRESHOLD = 200 // px/sec — lower = more sensitive fixation detection

type GazeCallback = (state: GazeState) => void

export class GazeTracker {
  private callback: GazeCallback | null = null
  private fixationStart: number | null = null
  private lastFilteredPoint: GazePoint | null = null
  private mode: 'webcam' | 'mouse' | 'off' = 'off'
  private mouseHandler: ((e: MouseEvent) => void) | null = null
  private webgazerInstance: any = null
  private gazeFilter: OneEuroFilter2D | null = null

  /**
   * Start webcam eye tracking via WebGazer.js.
   */
  async startWebcam(callback: GazeCallback): Promise<boolean> {
    this.callback = callback

    // WebGazer requires HTTPS (or localhost) for getUserMedia
    if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
      console.warn('Eye tracking requires HTTPS. Please access GazeWeave over HTTPS.')
      return false
    }

    // Create persistent status element for debugging
    const statusEl = document.createElement('div')
    statusEl.id = 'gaze-debug'
    statusEl.style.cssText = 'position:fixed;bottom:10px;left:10px;right:10px;background:#1e293b;color:#38bdf8;padding:12px 16px;border-radius:8px;z-index:999999;font:12px monospace;max-height:300px;overflow:auto;border:1px solid #334155;white-space:pre-wrap;'
    statusEl.textContent = '[1/4] Importing WebGazer module...'
    document.body.appendChild(statusEl)

    const log = (msg: string) => {
      console.log('[GazeTracker]', msg)
      if (statusEl.parentNode) {
        statusEl.textContent += '\n' + msg
        statusEl.scrollTop = statusEl.scrollHeight
      }
    }

    try {
      // WebGazer loaded via <script> tag (UMD) — available as window.webgazer
      const wg = (window as any).webgazer
      if (!wg) throw new Error('window.webgazer not found — is webgazer.js loaded?')
      this.webgazerInstance = wg
      log('[2/4] WebGazer found on window. Setting MediaPipe WASM path...')

      // WebGazer's pre-built dist includes mediapipe/ alongside it
      this.webgazerInstance.params.faceMeshSolutionPath = '/mediapipe/face_mesh'

      // Initialize One Euro Filter for gaze smoothing
      this.gazeFilter = new OneEuroFilter2D(
        GAZE_FILTER_MIN_CUTOFF,
        GAZE_FILTER_BETA,
        GAZE_FILTER_DCUTOFF,
      )

      let gazeCount = 0

      this.webgazerInstance
        .setRegression('ridge')
        .setGazeListener((data: { x: number; y: number } | null) => {
          gazeCount++
          if (statusEl.parentNode && gazeCount <= 5) {
            log(`Gaze frame #${gazeCount}: ${data ? `x=${Math.round(data.x)} y=${Math.round(data.y)}` : 'null (no face?)'}`)
            if (gazeCount === 5) {
              log('(Suppressing further gaze logs)')
            }
          }
          if (data && data.x !== null && data.y !== null) {
            this.onRawGazePoint(data.x, data.y)
          }
        })

      log('[3/4] Calling webgazer.begin() — this loads camera + face model...')

      await this.webgazerInstance.begin()

      log('[4/4] ✅ WebGazer started! Face detection active.')

      // Enable Kalman filter for extra smoothing (WebGazer's built-in)
      this.webgazerInstance.applyKalmanFilter(true)

      // Transition status element to compact mode after success
      statusEl.style.cssText = 'position:fixed;bottom:10px;left:10px;background:green;color:white;padding:8px 12px;border-radius:6px;z-index:99999;font:13px monospace;pointer-events:none;'
      statusEl.id = 'gaze-compact'
      statusEl.textContent = 'WebGazer + One Euro Filter active'
      // Auto-hide after 5s
      setTimeout(() => statusEl.remove(), 5000)

      // Show video preview but hide debug overlays
      this.webgazerInstance.showVideoPreview(true)
      this.webgazerInstance.showPredictionPoints(false)
      this.webgazerInstance.showFaceOverlay(false)
      this.webgazerInstance.showFaceFeedbackBox(false)

      this.mode = 'webcam'
      return true
    } catch (e: any) {
      const errMsg = e?.message || e?.toString?.() || JSON.stringify(e) || 'Unknown error'
      const errStack = e?.stack || '(no stack trace)'
      console.error('[GazeTracker] WebGazer failed:', e)

      log(`\n❌ ERROR: ${errMsg}`)
      log(errStack)
      statusEl.style.color = '#f87171'
      statusEl.style.borderColor = '#f87171'
      setTimeout(() => statusEl.remove(), 60000)

      return false
    }
  }

  /**
   * Start mouse-based tracking (fallback).
   */
  startMouse(callback: GazeCallback) {
    this.callback = callback
    this.gazeFilter = new OneEuroFilter2D(MOUSE_FILTER_MIN_CUTOFF, MOUSE_FILTER_BETA)
    this.mouseHandler = (e: MouseEvent) => {
      this.onRawGazePoint(e.clientX, e.clientY)
    }
    window.addEventListener('mousemove', this.mouseHandler)
    this.mode = 'mouse'
  }

  /**
   * Process a raw gaze/mouse coordinate through the One Euro Filter.
   */
  private onRawGazePoint(rawX: number, rawY: number) {
    const now = performance.now()

    // Apply One Euro Filter
    let x = rawX
    let y = rawY
    if (this.gazeFilter) {
      const filtered = this.gazeFilter.filter(rawX, rawY, now)
      x = filtered.x
      y = filtered.y
    }

    const point: GazePoint = { x, y, timestamp: now }

    // Compute velocity from filtered points
    const velocity = this.lastFilteredPoint
      ? this.computeVelocity(this.lastFilteredPoint, point)
      : 0

    const isFixating = velocity < SACCADE_VELOCITY_THRESHOLD

    if (isFixating) {
      if (!this.fixationStart) {
        this.fixationStart = now
      }
    } else {
      this.fixationStart = null
    }

    const fixationDuration = this.fixationStart ? now - this.fixationStart : 0

    this.lastFilteredPoint = point

    const state: GazeState = {
      currentPoint: point,
      fixationLine: null, // computed by render loop
      fixationParagraph: null,
      dwellStartTime: fixationDuration >= FIXATION_MIN_DURATION_MS ? this.fixationStart : null,
      isFixating: fixationDuration >= FIXATION_MIN_DURATION_MS,
      velocity,
    }

    // Write to bridge (hot path — no React involved)
    gazeBridge.update(state)

    // Also fire callback for non-render consumers
    if (this.callback) {
      this.callback(state)
    }
  }

  /**
   * Feed a calibration click to WebGazer.
   */
  recordCalibrationClick(x: number, y: number) {
    if (this.webgazerInstance) {
      this.webgazerInstance.recordScreenPosition(x, y, 'click')
    }
  }

  pause() {
    if (this.webgazerInstance) this.webgazerInstance.pause()
  }

  resume() {
    if (this.webgazerInstance) this.webgazerInstance.resume()
  }

  private computeVelocity(a: GazePoint, b: GazePoint): number {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const dt = (b.timestamp - a.timestamp) / 1000
    if (dt === 0) return 0
    return Math.sqrt(dx * dx + dy * dy) / dt
  }

  stop() {
    if (this.mouseHandler) {
      window.removeEventListener('mousemove', this.mouseHandler)
      this.mouseHandler = null
    }
    if (this.webgazerInstance) {
      try { this.webgazerInstance.end() } catch { /* ignore */ }
      this.webgazerInstance = null
    }
    // Clean up debug elements
    document.getElementById('gaze-debug')?.remove()
    document.getElementById('gaze-compact')?.remove()

    this.mode = 'off'
    this.gazeFilter?.reset()
    this.gazeFilter = null
    this.callback = null
    this.fixationStart = null
    this.lastFilteredPoint = null
    gazeBridge.clear()
  }

  getMode() { return this.mode }
  isWebcamActive() { return this.mode === 'webcam' && this.webgazerInstance !== null }
}

export const gazeTracker = new GazeTracker()
